import { neueKennung } from '@netcon-work/notizbuch-kern'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { trpc } from './anschluss'
import type { SpeichernEingabe } from './daten'

/**
 * **Entwürfe aus dem Browser nachreichen** (Nils, 05.11.2026: *„wenn ich da was erfasse, kein
 * internet habe, das es dann nicht gelöscht ist sondern beim nächsten mal dann hochgeladen und
 * gespeichert wird"*).
 *
 * Das Blatt legt jeden Stand sofort als Entwurf im Browser ab (`blatt.tsx`, „Entwurf im Browser").
 * Bisher kam er nur zurück, wenn man genau dieses Blatt wieder öffnete – ein neues Blatt, das nie
 * beim Server ankam, stand aber in keiner Liste, und passte der Server nicht mehr zum Entwurf,
 * wurde er verworfen. Jetzt:
 *
 * - **Von selbst nach:** beim Start von Work, wenn das Netz zurückkommt und wenn die App wieder in
 *   den Vordergrund kommt, geht jeder Entwurf raus, dessen Blatt gerade nirgends offen ist – auch
 *   neue Blätter.
 * - **Nichts wird verworfen:** hat sich die Notiz inzwischen woanders geändert (anderes Gerät,
 *   Plaud), überschreibt der Entwurf sie nicht, sondern kommt als eigene Notiz „… (offline
 *   geschrieben)" daneben, mit dem Hinweis, sie zusammenzuführen.
 *
 * Passwörter sind schon maskiert, bevor der Entwurf in den Browser kommt (E-420).
 */

const VORSILBE = 'notizbuch:entwurf:'
const OFFEN = 'notizbuch:blatt-offen:'
/** So oft meldet ein offenes Blatt, dass es noch da ist – auch für andere Reiter. */
const PULS_MS = 20_000
export const KONFLIKT_TAG = 'offline-konflikt'
const ALT_MS = 7 * 86_400_000

export interface Entwurf {
  titel: string
  inhalt: string
  /** Der Server-Stand, auf dem der Entwurf aufbaut. */
  basis: string
  zeit: number
  /** Alles, was das Blatt schicken würde – damit geht der Entwurf auch ungeöffnet raus (ab 05.11.). */
  eingabe?: SpeichernEingabe
  /** Nie beim Server angekommen. */
  neu?: boolean
}

export const entwurfSchluessel = (nb: string, id: string) => `${VORSILBE}${nb}:${id}`

export function entwurfLesen(schluessel: string): Entwurf | null {
  try {
    const e = JSON.parse(localStorage.getItem(schluessel) ?? 'null') as Entwurf | null
    return e && typeof e.inhalt === 'string' && typeof e.basis === 'string' ? e : null
  } catch {
    return null
  }
}

/** `false`, wenn der Browser nichts speichern lässt. */
export function entwurfSchreiben(schluessel: string, e: Entwurf): boolean {
  try {
    localStorage.setItem(schluessel, JSON.stringify(e))
    return true
  } catch {
    return false
  }
}

export function entwurfWeg(schluessel: string) {
  try {
    localStorage.removeItem(schluessel)
  } catch {
    /* egal */
  }
}

/** Alle Entwürfe im Browser. Exportiert für Tests. */
export function alleEntwuerfe(): { nb: string; id: string; schluessel: string; e: Entwurf }[] {
  const aus: { nb: string; id: string; schluessel: string; e: Entwurf }[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const schluessel = localStorage.key(i)
      if (!schluessel?.startsWith(VORSILBE)) continue
      const [nb, id] = schluessel.slice(VORSILBE.length).split(':')
      const e = entwurfLesen(schluessel)
      if (nb && id && e) aus.push({ nb, id, schluessel, e })
    }
  } catch {
    /* gesperrt – dann gibt es nichts nachzureichen */
  }
  return aus
}

/**
 * Ein offenes Blatt kümmert sich selbst um seinen Entwurf. Es meldet sich hier an (auch für andere
 * Reiter sichtbar) und beim Schließen wieder ab.
 */
export function blattOffen(schluessel: string): () => void {
  const puls = () => {
    try {
      localStorage.setItem(OFFEN + schluessel, String(Date.now()))
    } catch {
      /* egal */
    }
  }
  puls()
  const t = setInterval(puls, PULS_MS)
  return () => {
    clearInterval(t)
    try {
      localStorage.removeItem(OFFEN + schluessel)
    } catch {
      /* egal */
    }
  }
}

function istOffen(schluessel: string): boolean {
  try {
    return Date.now() - Number(localStorage.getItem(OFFEN + schluessel) ?? 0) < 3 * PULS_MS
  } catch {
    return false
  }
}

/**
 * Was mit einem Entwurf geschieht, gemessen am Server (`null` = dort gibt es die Notiz nicht).
 * Exportiert für Tests.
 */
export function entscheiden(
  e: Entwurf,
  server: { titel: string; inhalt: string } | null,
  jetzt = Date.now(),
): 'senden' | 'kopie' | 'weg' {
  if (server && e.inhalt === server.inhalt && (!e.titel || e.titel === server.titel)) return 'weg'
  if (server && e.basis === server.inhalt) return 'senden'
  if (!server && e.neu) return 'senden'
  // Entwürfe von vor dem 05.11. galten nach 7 Tagen als verfallen – meist Reste gelöschter Notizen
  if (!e.eingabe && jetzt - e.zeit > ALT_MS) return 'weg'
  return 'kopie'
}

/** Was der Entwurf als Speichern schickt – neuester Text vor der Eingabe von damals. */
export function eingabeAus(nb: string, id: string, e: Entwurf): SpeichernEingabe {
  // Ohne Zeitmarken: die gehören zu dem Text, den das Blatt damals hatte
  const rest: SpeichernEingabe = {
    ...(e.eingabe ?? { notizbuchId: nb, id, titel: '', inhalt: '' }),
  }
  delete rest.zeitmarken
  return {
    ...rest,
    notizbuchId: nb,
    id,
    titel: e.titel.trim() || rest.titel || 'Ohne Titel',
    inhalt: e.inhalt,
  }
}

/** Der Entwurf als eigene Notiz neben der geänderten. Exportiert für Tests. */
export function kopieAus(
  nb: string,
  e: Entwurf,
  server: { titel: string; mappe: string | null; art: string } | null,
): SpeichernEingabe {
  const basis = eingabeAus(nb, neueKennung(), e)
  const titel = basis.titel === 'Ohne Titel' && server ? server.titel : basis.titel
  const wann = new Date(e.zeit).toLocaleString('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
  const grund = server
    ? `„${server.titel}" wurde inzwischen woanders geändert`
    : 'die Notiz gibt es inzwischen nicht mehr'
  return {
    ...basis,
    titel: `${titel} (offline geschrieben)`.slice(0, 300),
    inhalt: `> **Offline geschrieben am ${wann}** – ${grund}. Damit nichts verloren geht, steht der Text hier. Bitte zusammenführen und diese Notiz dann löschen.\n\n${e.inhalt}`,
    art: basis.art ?? server?.art ?? 'notiz',
    mappe: basis.mappe !== undefined ? basis.mappe : (server?.mappe ?? null),
    datum: new Date(e.zeit).toISOString(),
    tags: [...new Set([...(basis.tags ?? []), KONFLIKT_TAG])],
  }
}

/** Legt den Entwurf als eigene Notiz ab und löscht ihn im Browser. Für das Blatt beim Öffnen. */
export async function alsKopieAblegen(
  nb: string,
  schluessel: string,
  e: Entwurf,
  server: { titel: string; mappe: string | null; art: string } | null,
): Promise<string> {
  const kopie = kopieAus(nb, e, server)
  await trpc.notizbuch.notiz.speichern.mutate(kopie)
  entwurfWeg(schluessel)
  return kopie.titel
}

let laeuft = false

/**
 * Jeden Entwurf, dessen Blatt nirgends offen ist, zum Server bringen. Was scheitert (kein Netz,
 * keine Rechte), bleibt liegen und kommt beim nächsten Anlass wieder dran. Liefert die Notizbücher,
 * in denen sich etwas geändert hat.
 */
export async function entwuerfeNachreichen(): Promise<Set<string>> {
  const geaendert = new Set<string>()
  if (laeuft || (typeof navigator !== 'undefined' && navigator.onLine === false)) return geaendert
  laeuft = true
  try {
    for (const { nb, id, schluessel, e } of alleEntwuerfe()) {
      if (istOffen(schluessel)) continue
      let server: { titel: string; inhalt: string; mappe: string | null; art: string } | null
      try {
        const n = await trpc.notizbuch.notiz.lesen.query({ notizbuchId: nb, id })
        server = {
          titel: n.kopf.titel,
          inhalt: n.inhalt,
          mappe: n.kopf.mappe ?? null,
          art: n.kopf.art,
        }
      } catch (f) {
        if ((f as { data?: { code?: string } }).data?.code !== 'NOT_FOUND') continue
        server = null
      }
      // Während der Abfrage geöffnet oder weitergeschrieben? Dann beim nächsten Mal.
      if (istOffen(schluessel) || entwurfLesen(schluessel)?.zeit !== e.zeit) continue
      try {
        const was = entscheiden(e, server)
        if (was === 'senden') await trpc.notizbuch.notiz.speichern.mutate(eingabeAus(nb, id, e))
        if (was === 'kopie') await alsKopieAblegen(nb, schluessel, e, server)
        entwurfWeg(schluessel)
        if (was !== 'weg') geaendert.add(nb)
      } catch {
        /* nächster Anlass */
      }
    }
  } finally {
    laeuft = false
  }
  return geaendert
}

/**
 * Läuft einmal in Work (Seitenleiste, `index.tsx`): beim Start, wenn das Netz zurückkommt, wenn die
 * App wieder sichtbar wird, und jede Minute, solange etwas wartet.
 */
export function useEntwuerfeNachreichen(aktiv: boolean) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!aktiv) return
    const los = () => {
      if (!alleEntwuerfe().length) return
      void entwuerfeNachreichen().then((nbs) => {
        for (const nb of nbs) void qc.invalidateQueries({ queryKey: ['notizbuch', 'liste', nb] })
      })
    }
    const sichtbar = () => document.visibilityState === 'visible' && los()
    los()
    const t = setInterval(los, 60_000)
    window.addEventListener('online', los)
    document.addEventListener('visibilitychange', sichtbar)
    return () => {
      clearInterval(t)
      window.removeEventListener('online', los)
      document.removeEventListener('visibilitychange', sichtbar)
    }
  }, [aktiv, qc])
}
